import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import Sidebar from 'pages/sideBar/SideBar';
import { formatDate, formatTimeInTimezone } from 'pages/utils/formdatedynamics';
import { getReadableOrderId } from 'pages/utils/HelperFun';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import moment from 'moment';
import {
  useSellerPackageOrders,
  filterPackageOrders,
  summarizePackages,
  fetchPackageOrderDetails,
  PackageOrderDialog,
  EMPTY_PACKAGE_SUMMARY,
  fmtAED,
  packageMoney,
  packageName,
  pct,
} from './SellerPackageOrdersOverview';

const DASHBOARD_API = 'https://backend.washta.com/api/seller/dashboard-overview';

const EMPTY_OVERVIEW = {
  filters: {
    period: 'month',
    periodLabel: 'This Month',
    locationKey: 'all',
    locationLabel: 'All locations',
  },
  locationOptions: {
    all: { label: 'All locations', value: 'all' },
    groups: [],
  },
  summary: {
    totalShops: 0,
    totalLocations: 0,
    totalReviews: 0,
    averageRating: 0,
  },
  stats: {
    orders: 0,
    accepted: 0,
    cancelled: 0,
    revenue: 'AED 0',
    payout: 'AED 0',
    grossRaw: 0,
    commission: 0,
    avgOrder: 'AED 0',
    avgOrderDelta: 'Since launch',
    newCustomers: 0,
    totalCust: 0,
    repeatRate: '0%',
    ordersDelta: 'Since launch',
    acceptedDelta: 'Since launch',
    cancelledDelta: 'Since launch',
    totalCompletedOrders: 0,
    totalAcceptedOrders: 0,
    totalCancelledOrders: 0,
    totalNumberOfOrders: 0,
  },
  charts: {
    peakHours: [],
    daysOfWeek: [],
    serviceMix: [],
    carTypes: [],
    addOns: [],
    ratingTrend: [],
    repeatRates: [],
    timingLocations: [],
    locationTable: [],
  },
  latestOrders: [],
  latestReviews: [],
};

const moneyValue = (value) => {
  if (typeof value === 'string' && value.trim()) {
    const trimmed = value.trim();
    if (/^AED\s/i.test(trimmed)) return trimmed;
    const numeric = Number(trimmed.replace(/[^\d.-]/g, ''));
    if (Number.isFinite(numeric)) {
      return `AED ${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
        maximumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
      }).format(numeric)}`;
    }
    return trimmed;
  }
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return 'AED 0';
  const decimals = !Number.isInteger(numeric);
  return `AED ${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals ? 2 : 0,
    maximumFractionDigits: decimals ? 2 : 0,
  }).format(numeric)}`;
};

const moneyOnly = (value) => {
  if (typeof value === 'string') {
    const numeric = Number(value.replace(/[^\d.-]/g, ''));
    return Number.isFinite(numeric) ? numeric : 0;
  }
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

const percentValue = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace('%', ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const currencyDeltaClass = (delta) => {
  if (!delta || delta === 'Since launch') return '';
  if (typeof delta !== 'string') return '';
  if (delta.startsWith('↑')) return 'up';
  if (delta.startsWith('↓')) return 'down';
  return '';
};

export const formatOrderTime = (value) => {
  if (!value) return "N/A";

  const m = moment.utc(value);
  if (!m.isValid()) return "N/A";

  const local = m.local();
  return `${local.format("MMM DD")} at ${local.format("hh:mm A")}`;
};

const getOrderService = (order) =>
  order?.serviceName ||
  order?.serviceVariant?.variantName ||
  order?.serviceVariant?.serviceName ||
  'Standard Service';

const getOrderLocation = (order) =>
  order?.shopLocationText ||
  order?.shopId?.shopDetails ||
  order?.shopId?.shopName ||
  'Location';

const getOrderCustomerName = (order) =>
  order?.customerId?.username ||
  order?.customerId?.fullName ||
  order?.customerId?.name ||
  'Anonymous';

const getLocationLabel = (options, selected) => {
  if (!options || selected === 'all') return options?.all?.label || 'All locations';
  for (const group of options.groups || []) {
    const match = group.shops?.find((shop) => shop.value === selected);
    if (match) return match.label;
  }
  return options?.all?.label || 'All locations';
};

const sanitizeFileNamePart = (value) =>
  String(value || '')
    .replace(/📍/g, '')
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'export';

function StarRating({ value }) {
  const rating = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <div className="review-stars">
      {[1, 2, 3, 4, 5].map((index) => (
        <span
          key={index}
          className="star"
          style={{ color: index <= rating ? '#f39d00' : '#ddd' }}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function LocationDropdown({ selected, options, onSelect, disabled }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleClick = (event) => {
      const target = event.target;
      const wrapper = document.getElementById('seller-location-dropdown');
      if (wrapper && !wrapper.contains(target)) setOpen(false);
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const label = getLocationLabel(options, selected);

  return (
    <div className="loc-dropdown-wrapper" id="seller-location-dropdown">
      <button
        type="button"
        className="loc-dropdown-btn"
        onClick={() => setOpen((state) => !state)}
        disabled={disabled}
      >
        {label}
        <span className={`chevron ${open ? 'open' : ''}`}>▼</span>
      </button>
      {open && !disabled && (
        <div className="loc-dropdown-menu">
          <div
            className={`loc-menu-item all-opt ${selected === 'all' ? 'selected' : ''}`}
            onClick={() => {
              onSelect('all');
              setOpen(false);
            }}
          >
            {options?.all?.label || 'All locations'}
          </div>
          {(options?.groups || []).map((group) => (
            <div key={group.city || 'group'}>
              <div className="loc-menu-group-label">{group.city}</div>
              {(group.shops || []).map((shop) => (
                <div
                  key={shop.value}
                  className={`loc-menu-item ${selected === shop.value ? 'selected' : ''}`}
                  onClick={() => {
                    onSelect(shop.value);
                    setOpen(false);
                  }}
                >
                  {shop.label}
                  <span style={{ fontSize: 10, color: '#aaa', marginLeft: 6 }}>
                    {shop.sub}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="phase2-block" style={{ marginTop: 16 }}>
      <div className="phase2-icon">...</div>
      <div className="phase2-title">Loading dashboard data</div>
      <div className="phase2-sub">
        Pulling the latest seller metrics from the backend.
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="phase2-block" style={{ marginTop: 16 }}>
      <div className="phase2-icon">!</div>
      <div className="phase2-title">Unable to load dashboard</div>
      <div className="phase2-sub">{message}</div>
      <button
        type="button"
        className="export-btn"
        style={{ marginTop: 12 }}
        onClick={onRetry}
      >
        Retry
      </button>
    </div>
  );
}

function DashboardView({ overview, period, onExport, packages = EMPTY_PACKAGE_SUMMARY }) {
  const filters = overview.filters || EMPTY_OVERVIEW.filters;
  const summary = overview.summary || EMPTY_OVERVIEW.summary;
  const stats = overview.stats || EMPTY_OVERVIEW.stats;
  const charts = overview.charts || EMPTY_OVERVIEW.charts;
  const latestOrders = overview.latestOrders || [];
  const latestReviews = overview.latestReviews || [];

  const peakHours = charts.peakHours || [];
  const daysOfWeek = charts.daysOfWeek || [];
  const serviceMix = charts.serviceMix || [];
  const carTypes = charts.carTypes || [];
  const addOns = charts.addOns || [];
  const ratingTrend = charts.ratingTrend || [];
  const repeatRates = charts.repeatRates || [];
  const timingLocations = charts.timingLocations || [];
  const locationRows = charts.locationTable || [];

  const currentMonthLabel = new Date().toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  });
  const periodLabel =
    period === 'month'
      ? currentMonthLabel
      : filters.periodLabel || 'All Time';

  const peakWindow = peakHours.find((item) => item.peak) || peakHours[0];
  const bestDay = daysOfWeek.find((item) => item.peak) || daysOfWeek[0];
  const trendStart = ratingTrend[0]?.val ?? 0;
  const trendEnd = ratingTrend[ratingTrend.length - 1]?.val ?? 0;
  const lowestRepeat = repeatRates.reduce(
    (lowest, item) =>
      !lowest || percentValue(item.pct) < percentValue(lowest.pct) ? item : lowest,
    null
  );
  const topService = serviceMix[0];
  const slowestLocation = timingLocations.reduce(
    (lowest, item) => {
      const total = moneyOnly(item.total);
      if (!lowest) return { item, total };
      return total > lowest.total ? { item, total } : lowest;
    },
    null
  );
  const [packageDetail, setPackageDetail] = useState(null);
  const openPackage = async (order) => {
    setPackageDetail(order);
    setPackageDetail(await fetchPackageOrderDetails(order));
  };

  // ── Shop orders + monthly packages, merged the same way as the super admin sales page ──
  // The API's revenue / commission / payout already include package sales (stats.packageRevenue,
  // not location-filtered), so take that out to get the shop-only figures; packages are added
  // back below from the package orders, which do follow the location filter.
  const shopGross = Math.max(0, moneyOnly(stats.revenue) - (Number(stats.packageRevenue) || 0));
  const shopCommission = shopGross * 0.08;
  const shopPayout = shopGross - shopCommission;
  const shopOrders = Number(stats.orders) || 0;
  const shopCompleted = Number(stats.accepted) || 0;
  const totalGross = shopGross + packages.gross;
  const totalCommission = shopCommission + packages.commission;
  const totalPayout = shopPayout + packages.payout;
  const totalOrders = shopOrders + packages.paidOrders.length;
  const totalCompleted = shopCompleted + packages.finishedCount;
  const totalCancelled = (Number(stats.cancelled) || 0) + packages.cancelledCount;
  const soldForAvg = shopCompleted + packages.soldCount;
  const avgOrder = fmtAED(soldForAvg ? totalGross / soldForAvg : 0);
  const splitNote = (shopPart, packagePart) => `Shop ${shopPart} · Packages ${packagePart}`;

  // Service mix by revenue: shop services + one row per package
  const mergedServiceMix = (() => {
    const rows = [
      ...serviceMix.map((service) => ({ ...service, amount: moneyOnly(service.amt), isPackage: false })),
      ...packages.byPackage.map((row) => ({
        label: `Package · ${row.label}`,
        amount: row.gross,
        amt: fmtAED(row.gross),
        sold: row.sold,
        isPackage: true,
      })),
    ];
    if (!packages.byPackage.length) return serviceMix;
    const total = rows.reduce((sum, row) => sum + row.amount, 0);
    return rows
      .map((row) => ({ ...row, pct: pct(row.amount, total) }))
      .sort((a, b) => b.amount - a.amount);
  })();
  const topPackage = packages.byPackage[0];

  // Car types: same bucket for shop and package orders (e.g. "SUV")
  const mergedCarTypes = (() => {
    if (!packages.byCarType.length) return carTypes;
    const map = new Map();
    carTypes.forEach((car) => map.set(String(car.label).toLowerCase(), { ...car, amount: moneyOnly(car.amt) }));
    packages.byCarType.forEach((row) => {
      const key = String(row.label).toLowerCase();
      const existing = map.get(key) || { label: row.label, color: '#92400E', amount: 0 };
      map.set(key, { ...existing, amount: existing.amount + row.gross });
    });
    const rows = [...map.values()];
    const total = rows.reduce((sum, row) => sum + row.amount, 0);
    return rows
      .map((row) => ({ ...row, amt: fmtAED(row.amount), pct: pct(row.amount, total) }))
      .sort((a, b) => b.amount - a.amount);
  })();

  const topLocation = locationRows[0];
  const latestPackageOrders = packages.paidOrders.slice(0, 8);

  return (
    <>
      <div className="kpi-strip">
        <div className="kpi-card purple">
          <div className="kpi-label">Total Orders</div>
          <div className="kpi-val purple">{totalOrders}</div>
          <div className="kpi-delta up">{stats.ordersDelta}</div>
        </div>
        <div className="kpi-card green">
          <div className="kpi-label">Completed Orders</div>
          <div className="kpi-val green">{totalCompleted}</div>
          <div className="kpi-delta up">{stats.acceptedDelta}</div>
        </div>
        <div className="kpi-card amber">
          <div className="kpi-label">Cancelled Orders</div>
          <div className="kpi-val amber">{totalCancelled}</div>
          <div className="kpi-delta">{stats.cancelledDelta}</div>
        </div>
        <div className="kpi-card dark">
          <div className="kpi-label">Avg Order Value</div>
          <div className="kpi-val white">{avgOrder}</div>
          <div className={`kpi-delta ${currencyDeltaClass(stats.avgOrderDelta)}`}>
            {stats.avgOrderDelta}
          </div>
        </div>
      </div>

      <div className="rev-box">
        <div className="rev-box-title">
          <span>
            Revenue breakdown · {filters.locationLabel || 'All locations'} · {periodLabel}
          </span>
          <span className="rev-box-sub">Commission rate per your platform agreement</span>
        </div>
        <div className="rev-row">
          <span className="rev-label">Shop orders · gross revenue (shop price × completed orders)</span>
          <span className="rev-amount">{fmtAED(shopGross)}</span>
        </div>
        <div className="rev-row">
          <span className="rev-label">
            Monthly packages · gross revenue (package price × {packages.soldCount} package{packages.soldCount === 1 ? '' : 's'} sold)
          </span>
          <span className="rev-amount">{fmtAED(packages.gross)}</span>
        </div>
        <div className="rev-row">
          <span className="rev-label" style={{ fontWeight: 600 }}>Total gross revenue</span>
          <span className="rev-amount" style={{ fontWeight: 700 }}>{fmtAED(totalGross)}</span>
        </div>
        <div className="rev-row">
          <span className="rev-label">
            Washta platform commission (8%) · {splitNote(fmtAED(shopCommission), fmtAED(packages.commission))}
          </span>
          <span className="rev-amount neg">- {fmtAED(totalCommission)}</span>
        </div>
        <div className="rev-row total">
          <span className="rev-label" style={{ fontWeight: 700, fontSize: 13 }}>
            Your payout this period
            <span style={{ display: 'block', fontSize: 10, fontWeight: 400, color: '#888', marginTop: 2 }}>
              {splitNote(fmtAED(shopPayout), fmtAED(packages.payout))}
            </span>
          </span>
          <span className="rev-amount big">{fmtAED(totalPayout)}</span>
        </div>
      </div>

      <div className="section-label">Demand & service insights</div>
      <div className="charts-grid-2">
        <div className="chart-wrap" style={{ marginBottom: 0 }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Peak hours · orders by time of day</div>
              <div className="chart-sub">Based on order completion time</div>
            </div>
          </div>
          <div className="chart-body">
            {peakHours.length > 0 ? (
              peakHours.map((hour) => (
                <div className="bar-row" key={hour.label}>
                  <span className="bar-label">{hour.label}</span>
                  <div className="bar-track">
                    <div
                      className={`bar-fill ${hour.peak ? 'hi' : ''}`}
                      style={{ width: `${hour.pct}%` }}
                    />
                  </div>
                  <span className="bar-val">{hour.val}</span>
                  {hour.peak && <span className="bar-note">peak</span>}
                </div>
              ))
            ) : (
              <div className="chart-footnote">No peak hour data available yet.</div>
            )}
            {peakWindow && (
              <div className="chart-footnote">
                Peak window: <strong>{peakWindow.label}</strong> · ensure full staff coverage
                during this window
              </div>
            )}
          </div>
        </div>

        <div className="chart-wrap" style={{ marginBottom: 0 }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Best day of week · avg orders per day</div>
              <div className="chart-sub">Last 30 days · all locations</div>
            </div>
          </div>
          <div className="chart-body">
            {daysOfWeek.length > 0 ? (
              daysOfWeek.map((day) => (
                <div className="bar-row" key={day.label}>
                  <span className="bar-label">{day.label}</span>
                  <div className="bar-track">
                    <div
                      className={`bar-fill ${day.peak ? 'hi' : ''}`}
                      style={{ width: `${day.pct}%` }}
                    />
                  </div>
                  <span className="bar-val">{day.val}</span>
                  {day.peak && <span className="bar-note">peak</span>}
                </div>
              ))
            ) : (
              <div className="chart-footnote">No day-of-week data available yet.</div>
            )}
            {bestDay && (
              <div className="chart-footnote">
                {bestDay.label} is the strongest day right now · plan staffing accordingly
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="charts-grid-2">
        <div className="chart-wrap" style={{ marginBottom: 0 }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Service mix · what customers are booking</div>
              <div className="chart-sub">Shop services and monthly packages · by revenue contribution</div>
            </div>
          </div>
          <div className="chart-body">
            {mergedServiceMix.length > 0 ? (
              mergedServiceMix.map((service) => (
                <div className="mix-row" key={service.label}>
                  <span className="mix-label">{service.label}</span>
                  <div className="mix-track">
                    <div
                      className="mix-fill"
                      style={{ width: `${service.pct}%`, ...(service.isPackage && { background: '#92400E' }) }}
                    />
                  </div>
                  <span className="mix-pct">{service.pct}%</span>
                  <span className="mix-amt" style={{ width: 110 }}>
                    {service.isPackage ? `${service.sold} sold · ${service.amt}` : service.amt}
                  </span>
                </div>
              ))
            ) : (
              <div className="chart-footnote">No service mix data yet.</div>
            )}
            {topService && (
              <div className="chart-footnote">
                Top service right now: <strong>{topService.label}</strong>
              </div>
            )}
            {topPackage && (
              <div className="chart-footnote">
                Best-selling package: <strong>{topPackage.label}</strong> · {topPackage.sold} sold · your payout{' '}
                {fmtAED(topPackage.payout)}
              </div>
            )}
          </div>
        </div>

        <div className="chart-wrap" style={{ marginBottom: 0 }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Car type split · Sedan vs SUV</div>
              <div className="chart-sub">Shop orders and monthly packages · by revenue contribution</div>
            </div>
          </div>
          <div className="chart-body">
            {mergedCarTypes.length > 0 ? (
              mergedCarTypes.map((car) => (
                <div className="mix-row" key={car.label} style={{ marginBottom: 12 }}>
                  <span className="mix-label" style={{ fontWeight: 600 }}>
                    {car.label}
                  </span>
                  <div className="mix-track" style={{ width: 160, height: 10 }}>
                    <div className="mix-fill" style={{ width: `${car.pct}%`, background: car.color }} />
                  </div>
                  <span className="mix-pct" style={{ color: car.color }}>
                    {car.pct}%
                  </span>
                  <span className="mix-amt">{car.amt}</span>
                </div>
              ))
            ) : (
              <div className="chart-footnote">No vehicle split data yet.</div>
            )}
            <div style={{ height: 1, background: '#f0f0f0', margin: '10px 0 12px' }} />
            <div style={{ fontSize: 10, fontWeight: 700, color: '#111', marginBottom: 8 }}>
              Add-on upsell rate
            </div>
            {addOns.length > 0 ? (
              addOns.map((addon) => (
                <div className="mix-row" key={addon.label}>
                  <span className="mix-label" style={{ fontSize: 10 }}>
                    {addon.label}
                  </span>
                  <div className="mix-track">
                    <div className="mix-fill" style={{ width: `${addon.pct}%`, background: '#1a6641' }} />
                  </div>
                  <span className="mix-pct" style={{ color: '#1a6641' }}>
                    {addon.pct}%
                  </span>
                  <span className="mix-amt" style={{ color: '#1a6641' }}>
                    {addon.note}
                  </span>
                </div>
              ))
            ) : (
              <div className="chart-footnote">No add-on upsell data yet.</div>
            )}
            <div className="chart-footnote">
              Upsell rate shows how often customers add extras - higher is better
            </div>
          </div>
        </div>
      </div>

      <div className="charts-grid-2">
        <div className="chart-wrap" style={{ marginBottom: 0 }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Rating trend · last 6 months</div>
              <div className="chart-sub">Average customer rating across all locations</div>
            </div>
          </div>
          <div className="chart-body">
            {ratingTrend.length > 0 ? (
              <>
                <div className="rating-trend-bars">
                  {ratingTrend.map((item) => (
                    <div className="rt-col" key={item.month}>
                      <div
                        className="rt-num"
                        style={item.top ? { color: '#4a3db8', fontWeight: 700 } : {}}
                      >
                        {item.val}
                      </div>
                      <div
                        className={`rt-bar ${item.top ? 'top' : item.hi ? 'hi' : ''}`}
                        style={{ height: `${item.val * 10}px` }}
                      />
                    </div>
                  ))}
                </div>
                <div className="rt-months">
                  {ratingTrend.map((item) => (
                    <div className="rt-month" key={item.month}>
                      {item.month}
                    </div>
                  ))}
                </div>
                <div className="chart-footnote" style={{ color: '#1a6641', fontWeight: 600 }}>
                  Average rating moved from {trendStart} to {trendEnd} over the last six months
                </div>
              </>
            ) : (
              <div className="chart-footnote">No rating trend data yet.</div>
            )}
          </div>
        </div>

        <div className="chart-wrap" style={{ marginBottom: 0 }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Repeat booking rate · per location</div>
              <div className="chart-sub">% of orders from customers who booked before</div>
            </div>
          </div>
          <div className="chart-body">
            {repeatRates.length > 0 ? (
              repeatRates.map((rate) => (
                <div className="bar-row" key={rate.label}>
                  <span className="bar-label wide">{rate.label}</span>
                  <div className="bar-track">
                    <div className={`bar-fill ${rate.color}`} style={{ width: `${rate.pct}%` }} />
                  </div>
                  <span
                    className="bar-val"
                    style={{
                      color: rate.color === 'green' ? '#1a6641' : rate.color === 'amber' ? '#d97706' : '#888',
                      fontWeight: rate.color ? 700 : 400,
                    }}
                  >
                    {rate.pct}%
                  </span>
                </div>
              ))
            ) : (
              <div className="chart-footnote">No repeat booking data yet.</div>
            )}
            {lowestRepeat && (
              <div className="chart-footnote">
                {lowestRepeat.label} at {lowestRepeat.pct}% is the lowest repeat rate - investigate service quality
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="section-label">Operational performance</div>
      <div className="chart-wrap">
        <div className="chart-head">
          <div>
            <div className="chart-title">Order timing · average time per stage</div>
            <div className="chart-sub">
              From customer booking received to wash completed · last 30 days
            </div>
          </div>
        </div>
        <div className="chart-body">
          <div className="timing-flow">
            <div className="timing-step">
              <div className="timing-lbl">Booking received</div>
              <div className="timing-dot" />
              <div className="timing-val muted">Start</div>
            </div>
            <div className="timing-arrow">→</div>
            <div className="timing-step">
              <div className="timing-lbl">Staff accepted</div>
              <div className="timing-dot" style={{ background: '#d97706' }} />
              <div className="timing-val warn">
                {timingLocations[0]?.accept || 'N/A'}
              </div>
            </div>
            <div className="timing-arrow">→</div>
            <div className="timing-step">
              <div className="timing-lbl">Wash completed</div>
              <div className="timing-dot" style={{ background: '#1a6641' }} />
              <div className="timing-val good">
                {timingLocations[0]?.complete || 'N/A'}
              </div>
            </div>
            <div className="timing-arrow">→</div>
            <div className="timing-step">
              <div className="timing-lbl">Total time</div>
              <div className="timing-dot" style={{ background: '#4a3db8' }} />
              <div className="timing-val total">
                {timingLocations[0]?.total || 'N/A'}
              </div>
            </div>
          </div>
          <div className="timing-note">
            {slowestLocation?.item ? (
              <>
                Slowest location currently is {slowestLocation.item.name} with {slowestLocation.item.total}
              </>
            ) : (
              'No timing data available yet.'
            )}
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th className="r">Accept time</th>
                    <th className="r">Completion time</th>
                    <th className="r">Total</th>
                    <th>Speed</th>
                  </tr>
                </thead>
                <tbody>
                  {timingLocations.length > 0 ? (
                    timingLocations.map((location) => (
                      <tr key={location.name}>
                        <td style={{ fontWeight: 600 }}>{location.name}</td>
                        <td
                          className="r"
                          style={{
                            fontWeight: 700,
                            color:
                              location.badge === 'done'
                                ? '#1a6641'
                                : location.badge === 'warn'
                                ? '#d97706'
                                : '#dc2626',
                          }}
                        >
                          {location.accept}
                        </td>
                        <td className="r">{location.complete}</td>
                        <td className="r" style={{ fontWeight: 700 }}>
                          {location.total}
                        </td>
                        <td>
                          <span className={`pill pill-${location.badge}`}>
                            {location.badgeLabel}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: 16 }}>
                        No timing rows yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="section-label">Location performance</div>
      <div className="sec-head">
        <div className="sec-title">Shop locations ranked by revenue · this period</div>
        <button type="button" className="export-btn" onClick={onExport}>
          ↓ Export
        </button>
      </div>
      <div className="chart-wrap" style={{ marginBottom: 22 }}>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th>
                <th>Location</th>
                <th className="r">Orders</th>
                <th className="r">Gross Revenue</th>
                <th className="r">Your Payout</th>
                <th className="r">Avg Rating</th>
                <th className="r">Accept Time</th>
                <th className="r">Repeat Rate</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {locationRows.length > 0 ? (
                locationRows.map((row) => (
                  <tr key={row.rank}>
                    <td style={{ fontWeight: 700, color: '#4a3db8' }}>{row.rank}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{row.name}</div>
                      <div style={{ fontSize: 9, color: '#aaa' }}>{row.sub}</div>
                    </td>
                    <td className="r">{row.orders}</td>
                    <td className="r">{row.gross}</td>
                    <td className="r" style={{ color: '#4a3db8', fontWeight: 700 }}>
                      {row.payout}
                    </td>
                    <td className="r" style={{ color: row.rating < 4 ? '#d97706' : undefined }}>
                      {row.rating ? `⭐ ${row.rating}` : 'N/A'}
                    </td>
                    <td
                      className="r"
                      style={{ color: row.acceptColor, fontWeight: 700 }}
                    >
                      {row.accept}
                    </td>
                    <td
                      className="r"
                      style={{ color: percentValue(row.repeat) < 50 ? '#d97706' : undefined }}
                    >
                      {row.repeat}
                    </td>
                    <td>
                      <span className={`pill pill-${row.status}`}>{row.statusLabel}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 16 }}>
                    No locations found yet.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>Shop orders total</td>
                <td className="r">{shopOrders}</td>
                <td className="r">{fmtAED(shopGross)}</td>
                <td className="r">{fmtAED(shopPayout)}</td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="sec-head">
        <div className="sec-title">Package locations ranked by revenue · this period</div>
      </div>
      <div className="chart-wrap" style={{ marginBottom: 22 }}>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th>
                <th>Location</th>
                <th className="r">Packages Sold</th>
                <th className="r">Active</th>
                <th className="r">Gross Revenue</th>
                <th className="r">Commission</th>
                <th className="r">Your Payout</th>
                <th className="r">Washes Delivered</th>
              </tr>
            </thead>
            <tbody>
              {packages.byLocation.length > 0 ? (
                packages.byLocation.map((row, index) => (
                  <tr key={row.key}>
                    <td style={{ fontWeight: 700, color: '#4a3db8' }}>{index + 1}</td>
                    <td style={{ fontWeight: 600 }}>{row.label}</td>
                    <td className="r">{row.sold}</td>
                    <td className="r">{row.active}</td>
                    <td className="r">{fmtAED(row.gross)}</td>
                    <td className="r" style={{ color: '#c0392b' }}>- {fmtAED(row.commission)}</td>
                    <td className="r" style={{ color: '#4a3db8', fontWeight: 700 }}>{fmtAED(row.payout)}</td>
                    <td className="r">{row.washesDone} / {row.washesTotal}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 16 }}>
                    No packages sold in this period.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>Packages total</td>
                <td className="r">{packages.soldCount}</td>
                <td className="r">{packages.activeCount}</td>
                <td className="r">{fmtAED(packages.gross)}</td>
                <td className="r">- {fmtAED(packages.commission)}</td>
                <td className="r">{fmtAED(packages.payout)}</td>
                <td className="r">{packages.washesDone} / {packages.washesTotal}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="sec-head">
        <div className="sec-title">
          Latest Shop Orders
          <span style={{ fontSize: 12, fontWeight: 500, marginLeft: 8, color: '#888' }}>
            · {filters.locationLabel || 'All locations'}
          </span>
        </div>
      </div>
      <div className="chart-wrap">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Service</th>
                <th>Location</th>
                <th className="r">Amount</th>
                <th>Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {latestOrders.length > 0 ? (
                latestOrders.map((order) => (
                  <tr key={order._id}>
                    <td style={{ fontWeight: 700, color: '#4a3db8', fontFamily: 'monospace' }}>
                      {getReadableOrderId(order._id)}
                    </td>
                    <td style={{ fontWeight: 600 }}>{getOrderCustomerName(order)}</td>
                    <td>{getOrderService(order)}</td>
                    <td style={{ color: '#888', fontSize: 11 }}>{getOrderLocation(order)}</td>
                    <td className="r" style={{ fontWeight: 700 }}>
                      {moneyValue(order.finalCost ?? order.cost)}
                    </td>
                    <td style={{ color: '#aaa', fontSize: 11 }}>
                      {formatOrderTime(order.date || order.createdAt)}
                    </td>
                    <td>
                      <span className={`pill pill-${order.status || 'new'}`}>
                        {order.status === 'completed'
                          ? 'Completed'
                          : order.status === 'inprocess'
                          ? 'In Process'
                          : order.status === 'cancelled'
                          ? 'Cancelled'
                          : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 16 }}>
                    No recent orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="sec-head">
        <div className="sec-title">
          Latest Package Orders
          <span style={{ fontSize: 12, fontWeight: 500, marginLeft: 8, color: '#888' }}>
            · {filters.locationLabel || 'All locations'}
          </span>
        </div>
      </div>
      <div className="chart-wrap">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Package</th>
                <th>Location</th>
                <th className="r">Washes</th>
                <th className="r">Price</th>
                <th className="r">Your Payout</th>
                <th>Paid</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {latestPackageOrders.length > 0 ? (
                latestPackageOrders.map((order) => (
                  <tr key={order._id} onClick={() => openPackage(order)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontWeight: 700, color: '#4a3db8', fontFamily: 'monospace' }}>
                      {order.orderId || getReadableOrderId(String(order._id), 'WS-PKG')}
                    </td>
                    <td style={{ fontWeight: 600 }}>{order.customerId?.fullName || 'Anonymous'}</td>
                    <td>{packageName(order)}</td>
                    <td style={{ color: '#888', fontSize: 11 }}>{order.locationDisplayName || 'Location'}</td>
                    <td className="r">{order.washesCompleted || 0} / {order.washCount || 0}</td>
                    <td className="r" style={{ fontWeight: 700 }}>{fmtAED(packageMoney(order).gross)}</td>
                    <td className="r" style={{ color: '#4a3db8', fontWeight: 700 }}>
                      {order.status === 'cancelled' ? '—' : fmtAED(packageMoney(order).payout)}
                    </td>
                    <td style={{ color: '#aaa', fontSize: 11 }}>{formatOrderTime(order.paidAt || order.createdAt)}</td>
                    <td>
                      <span className={`pill ${order.status === 'cancelled' ? 'pill-slow' : order.status === 'active' ? 'pill-new' : 'pill-done'}`}>
                        {order.status === 'active' ? 'Active' : order.status === 'cancelled' ? 'Cancelled' : order.status === 'expired' ? 'Expired' : 'Completed'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 16 }}>
                    No package orders in this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="section-label">Latest Reviews</div>
      <div className="reviews-wrap">
        <div className="reviews-head">
          <span style={{ fontSize: 18 }}>⭐</span>
          <span className="reviews-head-title">Latest Reviews</span>
          <span style={{ fontSize: 11, color: '#888', marginLeft: 4 }}>
            {latestReviews.length} reviews
          </span>
        </div>
        {latestReviews.length > 0 ? (
          latestReviews.map((review) => (
            <div className="review-item" key={review._id}>
              <div className="review-top">
                <span className="review-name">
                  {review.customerId?.email || review.customerId?.fullName || 'Anonymous'}
                </span>
                <StarRating value={Number(review.rating) || 0} />
              </div>
              {review.comment?.text && (
  <p className="paragph-heading addtion-adding">
    {review.comment.text}
  </p>
)}
              {/* <p className="review-comment">{review.comment?.text || 'No comment available'}</p> */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="review-date">
                  {review.date ? formatOrderTime(review.date) : 'N/A'}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div style={{ padding: 32, textAlign: 'center', color: '#aaa', fontSize: 12 }}>
            No reviews yet
          </div>
        )}
      </div>

      <div className="section-label">Dashboard summary</div>
      <div className="phase2-block">
        <div className="phase2-icon">i</div>
        <div className="phase2-title">Current snapshot</div>
        <div className="phase2-sub">
          {summary.totalLocations} locations, {summary.totalReviews} reviews, average rating{' '}
          {summary.averageRating || 0}. Top revenue location:{' '}
          {topLocation ? topLocation.name : 'N/A'}. Monthly packages sold: {packages.soldCount}
          {topPackage ? ` · best seller ${topPackage.label}` : ''}.
        </div>
      </div>
      {packageDetail && <PackageOrderDialog order={packageDetail} onClose={() => setPackageDetail(null)} />}
    </>
  );
}

function Dashboard() {
  const [period, setPeriod] = useState('month');
  const [location, setLocation] = useState('all');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadCount, setReloadCount] = useState(0);
  const navigate = useNavigate();
  // Monthly package orders are merged into every dashboard section next to the shop orders
  const { orders: packageOrders } = useSellerPackageOrders();
  const packages = useMemo(
    () => summarizePackages(filterPackageOrders(packageOrders, period, location)),
    [packageOrders, period, location]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchDashboard = async () => {
      setLoading(true);
      setError('');
      setOverview(null);

      try {
        const token = localStorage.getItem('accessToken');
        const response = await axios.get(
          `${DASHBOARD_API}?period=${encodeURIComponent(period)}&locationKey=${encodeURIComponent(location)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!mounted) return;

        const data = response.data?.data || {};
        setOverview({
          ...EMPTY_OVERVIEW,
          ...data,
          filters: { ...EMPTY_OVERVIEW.filters, ...(data.filters || {}) },
          locationOptions: {
            ...EMPTY_OVERVIEW.locationOptions,
            ...(data.locationOptions || {}),
          },
          summary: { ...EMPTY_OVERVIEW.summary, ...(data.summary || {}) },
          stats: { ...EMPTY_OVERVIEW.stats, ...(data.stats || {}) },
          charts: { ...EMPTY_OVERVIEW.charts, ...(data.charts || {}) },
          latestOrders: Array.isArray(data.latestOrders) ? data.latestOrders : [],
          latestReviews: Array.isArray(data.latestReviews) ? data.latestReviews : [],
        });
      } catch (err) {
        if (!mounted) return;
        if (err?.response?.status === 401) {
          localStorage.clear();
          navigate('/');
          return;
        }

        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Something went wrong while loading the dashboard.'
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchDashboard();

    return () => {
      mounted = false;
    };
  }, [period, location, reloadCount, navigate]);

  if (loading && !overview) {
    return (
      <div className="mainDiv-all-sideBar">
        <div className="div-sidebar">
          <Sidebar />
        </div>
        <div className="div-Dashbaord">
          <h1 className="h1-all-heading">Dashboard</h1>
          <div className="dashboard">
            <div className="styles-div" style={{ width: '100%' }}>
              <LoadingState />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !overview) {
    return (
      <div className="mainDiv-all-sideBar">
        <div className="div-sidebar">
          <Sidebar />
        </div>
        <div className="div-Dashbaord">
          <h1 className="h1-all-heading">Dashboard</h1>
          <div className="dashboard">
            <div className="styles-div" style={{ width: '100%' }}>
              <ErrorState
                message={error}
                onRetry={() => {
                  setError('');
                  setReloadCount((count) => count + 1);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activeOverview = overview || EMPTY_OVERVIEW;
  const locationOptions = activeOverview.locationOptions || EMPTY_OVERVIEW.locationOptions;
  const selectedLocationLabel =
    activeOverview.filters?.locationLabel ||
    getLocationLabel(locationOptions, location) ||
    'All locations';

  const handleExport = () => {
    const filters = activeOverview.filters || EMPTY_OVERVIEW.filters;
    const summary = activeOverview.summary || EMPTY_OVERVIEW.summary;
    const stats = activeOverview.stats || EMPTY_OVERVIEW.stats;
    const charts = activeOverview.charts || EMPTY_OVERVIEW.charts;

    const workbook = XLSX.utils.book_new();

    const overviewSheet = XLSX.utils.json_to_sheet([
      { Section: 'Filter', Key: 'Period', Value: filters.periodLabel || period },
      { Section: 'Filter', Key: 'Location', Value: selectedLocationLabel },
      { Section: 'Summary', Key: 'Total Shops', Value: summary.totalShops },
      { Section: 'Summary', Key: 'Total Locations', Value: summary.totalLocations },
      { Section: 'Summary', Key: 'Total Reviews', Value: summary.totalReviews },
      { Section: 'Summary', Key: 'Average Rating', Value: summary.averageRating },
      { Section: 'Stats', Key: 'Orders', Value: stats.orders },
      { Section: 'Stats', Key: 'Accepted', Value: stats.accepted },
      { Section: 'Stats', Key: 'Cancelled', Value: stats.cancelled },
      { Section: 'Stats', Key: 'Revenue', Value: stats.revenue },
      { Section: 'Stats', Key: 'Payout', Value: stats.payout },
      { Section: 'Stats', Key: 'Avg Order', Value: stats.avgOrder },
      { Section: 'Stats', Key: 'New Customers', Value: stats.newCustomers },
      { Section: 'Stats', Key: 'Total Customers', Value: stats.totalCust },
      { Section: 'Stats', Key: 'Repeat Rate', Value: stats.repeatRate },
      { Section: 'Packages', Key: 'Packages Sold', Value: packages.soldCount },
      { Section: 'Packages', Key: 'Active Packages', Value: packages.activeCount },
      { Section: 'Packages', Key: 'Cancelled Packages', Value: packages.cancelledCount },
      { Section: 'Packages', Key: 'Package Gross', Value: fmtAED(packages.gross) },
      { Section: 'Packages', Key: 'Package Commission', Value: fmtAED(packages.commission) },
      { Section: 'Packages', Key: 'Package Payout', Value: fmtAED(packages.payout) },
      { Section: 'Total', Key: 'Payout (shop + packages)', Value: fmtAED(moneyOnly(stats.payout) + packages.payout) },
    ]);
    XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Overview');

    const locationRows = (charts.locationTable || []).map((row) => ({
      Rank: row.rank,
      Location: row.name,
      SubLocation: row.sub,
      Orders: row.orders,
      GrossRevenue: row.gross,
      Payout: row.payout,
      AvgRating: row.rating || 'N/A',
      AcceptTime: row.accept,
      RepeatRate: row.repeat,
      Status: row.statusLabel,
    }));
    if (locationRows.length > 0) {
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(locationRows),
        'Location Performance'
      );
    }

    const orderRows = (activeOverview.latestOrders || []).map((order) => ({
      OrderId: getReadableOrderId(order._id),
      Customer: getOrderCustomerName(order),
      Service: getOrderService(order),
      Location: getOrderLocation(order),
      Amount: moneyValue(order.finalCost ?? order.cost),
      Time: formatOrderTime(order.date || order.createdAt),
      Status:
        order.status === 'completed'
          ? 'Completed'
          : order.status === 'inprocess'
          ? 'In Process'
          : order.status === 'cancelled'
          ? 'Cancelled'
          : 'Pending',
    }));
    if (orderRows.length > 0) {
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(orderRows),
        'Latest Orders'
      );
    }

    const packageRows = packages.paidOrders.map((order) => ({
      OrderId: order.orderId || getReadableOrderId(String(order._id), 'WS-PKG'),
      Customer: order.customerId?.fullName || '',
      Package: packageName(order),
      Location: order.locationDisplayName || '',
      CarType: order.carType || '',
      Washes: `${order.washesCompleted || 0} / ${order.washCount || 0}`,
      Status: order.status,
      PackagePrice: packageMoney(order).gross,
      Commission: packageMoney(order).commission,
      Payout: order.status === 'cancelled' ? 0 : packageMoney(order).payout,
      PaidAt: formatOrderTime(order.paidAt || order.createdAt),
    }));
    if (packageRows.length > 0) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(packageRows), 'Package Orders');
    }

    const reviewRows = (activeOverview.latestReviews || []).map((review) => ({
      Customer: review.customerId?.email || review.customerId?.fullName || 'Anonymous',
      Rating: Number(review.rating) || 0,
      Comment: review.comment?.text || '',
      Date: review.date ? formatOrderTime(review.date) : 'N/A',
    }));
    if (reviewRows.length > 0) {
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(reviewRows),
        'Latest Reviews'
      );
    }

    const fileName = `Washta_Dashboard_${sanitizeFileNamePart(period)}_${sanitizeFileNamePart(
      selectedLocationLabel
    )}.xlsx`;
    const workbookArray = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([workbookArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(downloadUrl);
  };

  return (
    <div className="mainDiv-all-sideBar">
      <div className="div-sidebar">
        <Sidebar />
      </div>
      <div className="div-Dashbaord">
        <h1 className="h1-all-heading">Dashboard</h1>
        <div className="dashboard">
          <main className="washta-main" style={{ width: '100%' }}>
            <div className="page-topbar" />

            <div className="filter-bar">
              <LocationDropdown
                selected={location}
                options={locationOptions}
                onSelect={setLocation}
                disabled={loading}
              />
              <div className="filter-sep" />
              <div className="date-tabs">
                {[
                  { id: 'today', label: 'Today' },
                  { id: 'week', label: 'This Week' },
                  { id: 'month', label: 'This Month' },
                  { id: 'all', label: 'All time' },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`date-tab ${period === item.id ? 'active' : ''}`}
                    onClick={() => setPeriod(item.id)}
                    disabled={loading}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <button type="button" className="export-btn" disabled={loading} onClick={handleExport}>
                ↓ Export
              </button>
            </div>

            {error ? (
              <ErrorState
                message={error}
                onRetry={() => {
                  setError('');
                  setReloadCount((count) => count + 1);
                }}
              />
            ) : null}

            <DashboardView overview={activeOverview} period={period} onExport={handleExport} packages={packages} />
          </main>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
